import { CartLinesUpdateEvent } from '@shopify/events';

(() => {
  if (window.CartRewardRuntimeLoaded) return;
  window.CartRewardRuntimeLoaded = true;

  // Tracks reward containers that have a fetch in flight, so we never fire twice.
  const inFlight = new WeakSet();

  const loadProducts = async (reward) => {
    const target = reward.querySelector('[data-reward-products]');
    const tag = reward.getAttribute('data-reward-tag');
    if (!(target instanceof HTMLElement) || !tag) return;

    // Already populated (gift list or empty-state present) — nothing to do. We key off the
    // actual rendered content rather than a data-flag, because the cart drawer is updated by
    // morphing, which can preserve stale attributes while resetting the children to "Loading…".
    if (target.querySelector('.cart-rewards__gift-list, .cart-rewards__empty')) return;
    if (inFlight.has(target)) return;
    inFlight.add(target);

    const root = window.Shopify?.routes?.root || '/';
    // Use Shopify's exact tag search. Predictive search is capped at 10 fuzzy matches and can
    // exclude every tagged reward product before the section's exact-match safeguard runs.
    const url = new URL(`${root}search`, window.location.origin);
    url.searchParams.set('q', `tag:${tag}`);
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'cart-reward-products');

    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Reward lookup failed with ${response.status}`);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = parsed.querySelector('[data-cart-reward-results]');
      if (!(results instanceof HTMLElement)) throw new Error('Reward results were missing');
      target.replaceChildren(...results.childNodes);
    } catch (error) {
      console.error('[cart-rewards] Failed to load reward products', error);
      target.textContent = 'Reward options are unavailable right now.';
    } finally {
      inFlight.delete(target);
    }
  };

  // Full-document scan. Cheap (a handful of elements) and, unlike hydrating only freshly-added
  // nodes, it reliably catches the reward tier that appears when the shopper crosses the spend
  // threshold inside the cart drawer — which arrives via an in-place morph, not an added node.
  const hydrate = () => {
    document.querySelectorAll('cart-rewards[data-reward-tag]').forEach(loadProducts);
  };

  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest?.('[data-reward-add]');
      if (!(button instanceof HTMLElement)) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;
      button.disabled = true;
      button.classList.add('is-loading');

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      if (sectionIds.length) body.append('sections', sectionIds.join(','));

      try {
        const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);

        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!cartResponse.ok) throw new Error(`Cart refresh failed: ${cartResponse.status}`);
        const cart = await cartResponse.json();
        const deferred = CartLinesUpdateEvent.createPromise();
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: deferred.promise,
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            source: 'cart-rewards',
            sourceId: 'cart-rewards',
            productId: button.getAttribute('data-product-id'),
            sections: data.sections,
            items: cart.items,
            itemCount: cart.item_count,
            didError: false,
          },
        });
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart } }));
      } catch (error) {
        console.error('[cart-rewards] Failed to add gift', error);
        const region = button.closest('cart-rewards')?.querySelector('[data-reward-error]');
        if (region instanceof HTMLElement) {
          region.textContent = error.message || 'Sorry, that gift could not be added. Please try again.';
          region.hidden = false;
        }
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    },
    true
  );

  // Debounce re-scans to one per animation frame so a burst of DOM/morph mutations only
  // triggers a single hydrate pass.
  let scheduled = false;
  const scheduleHydrate = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      hydrate();
    });
  };

  hydrate();

  // DO NOT REMOVE THIS OBSERVER. The reward tier is inserted into the cart drawer by an async
  // section MORPH (see morphSection in section-renderer.js), which frequently lands a frame or
  // more AFTER the cart:update event fires — and drawer-open / discount re-renders can update the
  // reward DOM with no cart event at all. Event listeners alone therefore miss it and the picker
  // stays stuck on "Loading…" (this regressed when the observer was dropped for INP reasons; the
  // gift list broke live twice as a result). The observer below is scoped to the two cart
  // container elements (not documentElement), so its INP cost is negligible while still catching
  // the morph whenever it lands.
  const observer = new MutationObserver(scheduleHydrate);
  const observed = new WeakSet();
  const attachObservers = () => {
    document.querySelectorAll('cart-drawer-component, cart-items-component').forEach((element) => {
      if (observed.has(element)) return;
      observed.add(element);
      observer.observe(element, { childList: true, subtree: true });
    });
  };
  attachObservers();

  // Deterministic signals from the theme whenever cart contents change. Also re-attach observers
  // in case a cart container was (re)added, then rescan.
  const onCartChange = () => {
    attachObservers();
    scheduleHydrate();
  };
  document.addEventListener('cart:update', onCartChange);
  document.addEventListener('cart:updated', onCartChange);

  // If this script ran before the cart containers were parsed, re-attach once the DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        attachObservers();
        hydrate();
      },
      { once: true }
    );
  }
})();
